import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { Observable, map, throwError } from 'rxjs';
import {
  BolsonLote,
  BolsonesFiltro,
  BolsonesRespSchema,
  AccionRespSchema,
  Destino,
  DestinosRespSchema,
  LiberacionBolsones,
  LoteAsignacion,
  LotesFiltro,
  LotesRespSchema,
  NuevaAsignacion,
  NuevaAsignacionResp,
} from '../models/lot-asignacion';
import { ContextService } from './context.service';

/** Claves del lote con los nombres del WS. */
const loteWs = (lote: BolsonesFiltro) => ({
  Producto_Codigo: lote.productCode,
  PLote_Codigo: lote.batchCode,
  PLote_Cosecha: lote.harvestYear,
  PLote_GruLot: lote.groupLot,
});

@Injectable({
  providedIn: 'root',
})
export class AsignacionesService {
  private readonly baseUrl = `${environment.apiUrl}/lot-assignments`;

  private readonly http = inject(HttpClient);
  private readonly context = inject(ContextService);

  public getDestinos(): Observable<Destino[]> {
    return this.get('/destinations', {}).pipe(map((res) => DestinosRespSchema.parse(res).data));
  }

  /** Lotes de la planta activa; los filtros se envían solo si tienen valor. */
  public getLotes(filtro: LotesFiltro = {}): Observable<LoteAsignacion[]> {
    return this.get('/lots', { ...filtro }).pipe(map((res) => LotesRespSchema.parse(res).data));
  }

  public getBolsones(lote: BolsonesFiltro): Observable<BolsonLote[]> {
    return this.get('/lots/bags', loteWs(lote)).pipe(map((res) => BolsonesRespSchema.parse(res)));
  }

  /** Compromete los bolsones al destino (`WS_SemComprometer`). */
  public creaAsignacion(asig: NuevaAsignacion): Observable<NuevaAsignacionResp> {
    return this.post('/commit', {
      ...loteWs(asig),
      Un01NroOpe: asig.un01NroOpe,
      PArea_Codigo: asig.areaCode,
      FechaReserva: asig.assignmentDate,
      Data: asig.secPros.map((SecPros) => ({ SecPros })),
    });
  }

  /** Libera los bolsones comprometidos a la operación (`WS_SemLiberar`). */
  public liberaBolsones(lib: LiberacionBolsones): Observable<NuevaAsignacionResp> {
    return this.post('/release', {
      ...loteWs(lib),
      Un01NroOpe: lib.un01NroOpe,
      Data: lib.secPros.map((SecPros) => ({ SecPros })),
    });
  }

  /** POST con empresa y planta de la planta activa; sin planta activa emite un error. */
  private post(path: string, body: Record<string, unknown>): Observable<NuevaAsignacionResp> {
    const planta = this.context.active();
    if (!planta) return throwError(() => new Error('No hay una planta activa'));
    return this.http
      .post<unknown>(this.baseUrl + path, { Empresa_Codigo: planta.companyCode, Planta_Codigo: planta.plantCode, ...body })
      .pipe(
        map((res) => {
          const parsed = AccionRespSchema.safeParse(res);
          if (!parsed.success) {
            console.warn(`${path} resp schema mismatch`, parsed.error);
            throw new Error('Respuesta inválida del servidor');
          }
          if (!parsed.data.success) throw new Error(parsed.data.message || 'El servidor rechazó la operación');
          return parsed.data;
        }),
      );
  }

  /** GET con empresa y planta de la planta activa; sin planta activa emite un error. */
  private get(path: string, query: Record<string, string | number | boolean | undefined>): Observable<unknown> {
    const planta = this.context.active();
    if (!planta) return throwError(() => new Error('No hay una planta activa'));
    let params = new HttpParams().set('Empresa_Codigo', planta.companyCode).set('Planta_Codigo', planta.plantCode);
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== '') params = params.set(key, value);
    }
    return this.http.get<unknown>(this.baseUrl + path, { params });
  }
}
